import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { GoogleAuthProvider, signInWithPopup, signInWithEmailAndPassword, signOut as fbSignOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db, ixoraoneAuth, ixoraoneDb } from '../firebase';
import { resolveAuthEmail } from '../lib/resolveAuthEmail';
import { isStaffAdminEmail } from '../lib/staffAdminEmail';
import { identifyUser, resetUser, track } from '../lib/telemetry';

const AdminCtx = createContext({
  isAdmin: false,
  isSiteManager: false,
  siteManagerAllotment: null,
  user: null,
  signingIn: false,
  signIn: () => {},
  signInWithEmployeeId: async () => {},
  signOut: () => {},
});

export function AdminProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSiteManager, setIsSiteManager] = useState(false);
  const [siteManagerAllotment, setSiteManagerAllotment] = useState(null);
  const [signingIn, setSigningIn] = useState(false);
  const bcRef = useRef(null);

  // Separate state for employee ID login (does not use feedback-app Firebase Auth).
  const [ixoraoneUser, setIxoraoneUser] = useState(() => {
    try {
      const stored = sessionStorage.getItem('ixoraone_admin_user');
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });

  // Cross-tab sign-out synchronisation via BroadcastChannel.
  useEffect(() => {
    bcRef.current = new BroadcastChannel('ixora_auth');
    bcRef.current.onmessage = (e) => {
      if (e.data === 'signout') {
        sessionStorage.removeItem('ixoraone_admin_user');
        setIxoraoneUser(null);
        setUser(null);
        setIsAdmin(false);
        setIsSiteManager(false);
        setSiteManagerAllotment(null);
        fbSignOut(auth).catch(() => {});
        fbSignOut(ixoraoneAuth).catch(() => {});
      }
    };
    return () => bcRef.current?.close();
  }, []);

  // Restore ixoraone session on mount — re-validate against Firestore to catch
  // deactivated accounts or revoked dashboard access since last visit (BG-06).
  useEffect(() => {
    if (!ixoraoneUser) return;

    let cancelled = false;
    (async () => {
      try {
        const profileSnap = await getDoc(doc(ixoraoneDb, 'users', ixoraoneUser.uid));
        if (cancelled) return;

        if (!profileSnap.exists()) {
          throw new Error('profile-deleted');
        }
        const profile = profileSnap.data();
        if (profile.deactivated === true || profile.feedbackDashboardAccess !== true) {
          throw new Error('access-revoked');
        }

        // Profile still valid — restore session.
        setUser({ email: ixoraoneUser.email, displayName: ixoraoneUser.name, isAnonymous: false, _ixoraone: true });
        setIsAdmin(true);
      } catch {
        // Session is stale or access was revoked — clear it silently.
        if (cancelled) return;
        sessionStorage.removeItem('ixoraone_admin_user');
        setIxoraoneUser(null);
        setUser(null);
        setIsAdmin(false);
        setIsSiteManager(false);
        setSiteManagerAllotment(null);
        fbSignOut(ixoraoneAuth).catch(() => {});
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      // If an ixoraone session is active, don't let Google auth state clobber it.
      if (ixoraoneUser) return;

      if (!u || u.isAnonymous) {
        setUser(null);
        setIsAdmin(false);
        setIsSiteManager(false);
        setSiteManagerAllotment(null);
        return;
      }
      const key = resolveAuthEmail(u);
      if (!key) {
        setUser(u);
        setIsAdmin(false);
        return;
      }
      setUser(u);
      let resolvedIsAdmin = false;
      try {
        if (isStaffAdminEmail(key)) {
          setIsAdmin(true);
          identifyUser(u.uid, { role: 'admin', source: 'google' });
          track('dashboard_login', { role: 'admin', method: 'google' });
          return;
        }
        const snap = await getDoc(doc(db, 'dashboardAdmins', key));
        resolvedIsAdmin = snap.exists();
        setIsAdmin(resolvedIsAdmin);
      } catch {
        setIsAdmin(false);
      }
      let resolvedIsSiteManager = false;
      try {
        const supSnap = await getDoc(doc(db, 'supervisors', u.uid));
        if (supSnap.exists() && supSnap.data().userRole === 'siteManager') {
          setIsSiteManager(true);
          setSiteManagerAllotment(supSnap.data().ixora_allotment || null);
          resolvedIsSiteManager = true;
        } else {
          setIsSiteManager(false);
          setSiteManagerAllotment(null);
        }
      } catch {
        setIsSiteManager(false);
        setSiteManagerAllotment(null);
      }
      // Identify after both admin + siteManager checks resolve.
      const role = resolvedIsAdmin ? 'admin' : resolvedIsSiteManager ? 'siteManager' : 'viewer';
      identifyUser(u.uid, { role, source: 'google' });
      track('dashboard_login', { role, method: 'google' });
    });
    return unsub;
  }, [ixoraoneUser]);

  const signIn = async () => {
    setSigningIn(true);
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      await signInWithPopup(auth, provider);
    } catch (err) {
      if (err?.code !== 'auth/popup-closed-by-user') {
        alert('Sign-in failed: ' + (err?.message || 'unknown error'));
      }
    } finally {
      setSigningIn(false);
    }
  };

  /**
   * Sign in using an IxoraOne Employee ID + password.
   * Authenticates against the ixora-one Firebase project, then checks
   * that the user has feedbackDashboardAccess: true in their profile.
   *
   * @throws {Error} with a user-facing message on failure.
   */
  const signInWithEmployeeId = async (employeeId, password) => {
    setSigningIn(true);
    try {
      const email = `${employeeId.trim().toLowerCase()}@ixoraone.local`;
      let cred;
      try {
        cred = await signInWithEmailAndPassword(ixoraoneAuth, email, password);
      } catch (primaryErr) {
        // Fallback to .internal domain for older accounts.
        if (primaryErr?.code === 'auth/user-not-found' || primaryErr?.code === 'auth/invalid-credential') {
          const fallbackEmail = `${employeeId.trim().toLowerCase()}@ixoraone.internal`;
          cred = await signInWithEmailAndPassword(ixoraoneAuth, fallbackEmail, password);
        } else {
          throw primaryErr;
        }
      }

      const uid = cred.user.uid;
      const profileSnap = await getDoc(doc(ixoraoneDb, 'users', uid));
      if (!profileSnap.exists()) throw new Error('Employee profile not found.');

      const profile = profileSnap.data();
      if (profile.deactivated === true) throw new Error('This account has been deactivated.');
      if (profile.feedbackDashboardAccess !== true) {
        throw new Error('Feedback dashboard access has not been granted for this account. Contact your administrator.');
      }

      const sessionUser = {
        uid,
        email: cred.user.email,
        name: profile.name || profile.displayName || employeeId,
        employeeId: profile.employeeId || employeeId,
      };
      sessionStorage.setItem('ixoraone_admin_user', JSON.stringify(sessionUser));
      setIxoraoneUser(sessionUser);
      setUser({ email: sessionUser.email, displayName: sessionUser.name, isAnonymous: false, _ixoraone: true });
      setIsAdmin(true);
      identifyUser(uid, { role: 'admin', name: sessionUser.name, employeeId: sessionUser.employeeId, source: 'employeeId' });
      track('dashboard_login', { role: 'admin', method: 'employeeId' });
    } finally {
      setSigningIn(false);
    }
  };

  const signOut = async () => {
    resetUser();
    track('dashboard_logout');
    sessionStorage.removeItem('ixoraone_admin_user');
    setIxoraoneUser(null);
    try { await fbSignOut(auth); } catch { /* ignore */ }
    try { await fbSignOut(ixoraoneAuth); } catch { /* ignore */ }
    setUser(null);
    setIsAdmin(false);
    // QA finding C1 — explicitly clear site-manager state to avoid
    // privilege persistence across sign-ins (don't rely on the auth
    // state listener since the ixoraone employee-ID path may not fire it).
    setIsSiteManager(false);
    setSiteManagerAllotment(null);
    bcRef.current?.postMessage('signout');
  };

  return (
    <AdminCtx.Provider value={{ isAdmin, isSiteManager, siteManagerAllotment, user, signingIn, signIn, signInWithEmployeeId, signOut }}>
      {children}
    </AdminCtx.Provider>
  );
}

export function useAdmin() {
  return useContext(AdminCtx);
}
